import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { fetchGallery } from "@/lib/api";
import SectionHeader from "@/components/SectionHeader";
import { Dialog, DialogContent, DialogTitle, DialogClose } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ChevronLeft, ChevronRight, X } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const resolveUrl = (url) => (url && url.startsWith("/uploads/") ? `${BACKEND}${url}` : url);

export default function GalleryPage() {
    const [items, setItems] = useState([]);
    const [filter, setFilter] = useState("all");
    const [openIdx, setOpenIdx] = useState(-1);

    useEffect(() => {
        fetchGallery().then(setItems).catch(() => setItems([]));
    }, []);

    const categories = useMemo(() => {
        const seen = new Set();
        items.forEach((i) => { if (i.category) seen.add(i.category); });
        return Array.from(seen).sort();
    }, [items]);

    const filtered = useMemo(
        () => (filter === "all" ? items : items.filter((i) => i.category === filter)),
        [items, filter]
    );

    const next = () => setOpenIdx((i) => (i + 1) % filtered.length);
    const prev = () => setOpenIdx((i) => (i - 1 + filtered.length) % filtered.length);

    return (
        <div data-testid="gallery-page">
            <section className="section">
                <div className="container-narrow">
                    <SectionHeader
                        eyebrow="Gallery"
                        title="Smoke. Plates. Crowds."
                        subtitle="A look at the food, the truck, and the events."
                    />

                    <Tabs value={filter} onValueChange={(v) => { setFilter(v); setOpenIdx(-1); }}>
                        <TabsList className="flex flex-wrap justify-start h-auto bg-black/40 border border-white/10 p-1 rounded-md gap-1 mb-6" data-testid="gallery-filter-tabs">
                            <TabsTrigger value="all" className="data-[state=active]:bg-orange-500/20 data-[state=active]:text-orange-300 text-white/70 px-3 py-1.5 text-xs uppercase tracking-[0.18em] font-display rounded">
                                All
                            </TabsTrigger>
                            {categories.map((cat) => (
                                <TabsTrigger key={cat} value={cat} className="data-[state=active]:bg-orange-500/20 data-[state=active]:text-orange-300 text-white/70 px-3 py-1.5 text-xs uppercase tracking-[0.18em] font-display rounded">
                                    {cat}
                                </TabsTrigger>
                            ))}
                        </TabsList>
                    </Tabs>

                    {/* Masonry */}
                    <div
                        className="columns-1 sm:columns-2 lg:columns-3 gap-4 [column-fill:_balance]"
                        data-testid="gallery-grid"
                    >
                        <AnimatePresence>
                            {filtered.map((g, idx) => (
                                <motion.button
                                    key={g.id}
                                    layout
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0 }}
                                    transition={{ duration: 0.4 }}
                                    onClick={() => setOpenIdx(idx)}
                                    className="mb-4 block w-full break-inside-avoid rounded-lg overflow-hidden border border-white/10 bg-black/30 group relative"
                                    data-testid="gallery-image-tile"
                                >
                                    <img
                                        src={resolveUrl(g.url)}
                                        alt={g.caption || "Smokin Mo's"}
                                        loading="lazy"
                                        className="w-full h-auto block transition-transform duration-500 group-hover:scale-[1.03]"
                                    />
                                    {g.caption && (
                                        <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-black/85 via-black/40 to-transparent text-left">
                                            <div className="text-sm text-white/90 font-medium">{g.caption}</div>
                                        </div>
                                    )}
                                </motion.button>
                            ))}
                        </AnimatePresence>
                    </div>

                    {filtered.length === 0 && (
                        <div className="text-white/60" data-testid="gallery-empty">No images yet.</div>
                    )}
                </div>
            </section>

            {/* Lightbox */}
            <Dialog open={openIdx >= 0} onOpenChange={(o) => !o && setOpenIdx(-1)}>
                <DialogContent className="max-w-[95vw] sm:max-w-3xl bg-black/85 border-white/10 p-0" data-testid="gallery-lightbox-dialog">
                    <DialogTitle className="sr-only">Gallery image</DialogTitle>
                    {openIdx >= 0 && filtered[openIdx] && (
                        <div className="relative">
                            <img
                                src={resolveUrl(filtered[openIdx].url)}
                                alt={filtered[openIdx].caption || "Smokin Mo's"}
                                className="lightbox-img w-full"
                            />
                            <button
                                onClick={prev}
                                className="absolute left-3 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/60 border border-white/10 hover:bg-black/80"
                                aria-label="Previous"
                                data-testid="gallery-lightbox-prev-button"
                            ><ChevronLeft size={20} /></button>
                            <button
                                onClick={next}
                                className="absolute right-3 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/60 border border-white/10 hover:bg-black/80"
                                aria-label="Next"
                                data-testid="gallery-lightbox-next-button"
                            ><ChevronRight size={20} /></button>
                            {filtered[openIdx].caption && (
                                <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black/85 via-black/40 to-transparent">
                                    <div className="text-sm text-white/90">{filtered[openIdx].caption}</div>
                                </div>
                            )}
                            <DialogClose asChild>
                                <button
                                    className="absolute left-3 top-3 z-[60] p-2 rounded-full bg-black/60 border border-white/10 hover:bg-black/80"
                                    aria-label="Close"
                                    data-testid="gallery-lightbox-close-button"
                                ><X size={18} /></button>
                            </DialogClose>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
